import { CircularProgress, Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { dateSVC } from "app/util/date/service"
import { ListActions } from "campaign/components/list/ListActions"
import { CampaignStatus } from "campaign/components/misc/CampaignStatus"
import { CAMPAIGN_STATUSES, ICampaign } from "campaign/model"
import { sortStatus } from "campaign/tools/status"
import { getOrderCampaigns } from "order/tools/campaign"
import { CAMPAIGN_SUMMARY_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { getDataGridTexts } from "ui/tools/list"
import { ListIdCell } from "../../ui/components/ListIdCell"

interface IOrderCampaignListProps {
  orderId: number
}

export const OrderCampaignList = React.memo(({ orderId }: IOrderCampaignListProps): JSX.Element => {
  const [campaigns, setCampaigns] = React.useState<null | ICampaign[]>(null)
  const [hasError, setHasError] = React.useState(false)
  // first render use effect
  React.useEffect(() => {
    getOrderCampaigns(orderId)
      .then(newCampaigns => setCampaigns(newCampaigns))
      .catch(error => {
        console.error("Unable to get order campaigns", error)
        setHasError(true)
      })
  }, [])
  const columns: Array<GridColDef<ICampaign>> = [
    {
      field: "product",
      headerName: translate("label.name"),
      minWidth: 180,
      flex: 4,
      renderCell: params => (
        <ListIdCell
          label={params.row.product || ""}
          id={params.row.id}
          description={params.row.description}
          onClick={() => navigate({ path: CAMPAIGN_SUMMARY_PATH, pathParams: params.row.id })}
        />
      )
    },
    {
      field: "status_id",
      headerName: translate("label.status"),
      align: "left",
      minWidth: 110,
      flex: 1,
      sortComparator: (_1, _2, cell1, cell2) =>
        sortStatus(cell1.value as CAMPAIGN_STATUSES, cell2.value as CAMPAIGN_STATUSES),
      renderCell: params => <CampaignStatus status={params.row.status_id} forList />
    },
    {
      field: "start_date",
      headerName: translate("label.startDate"),
      minWidth: 90,
      flex: 1,
      renderCell: params => (params.row.start_date ? dateSVC.format(params.row.start_date) : "")
    },
    {
      field: "end_date",
      headerName: translate("label.endDate"),
      minWidth: 90,
      flex: 1,
      renderCell: params => (params.row.end_date ? dateSVC.format(params.row.end_date) : "")
    },
    {
      field: "created_at",
      headerName: translate("label.creationDate"),
      minWidth: 100,
      flex: 1,
      renderCell: params => (params.row.created_at ? dateSVC.format(params.row.created_at) : "")
    },
    {
      field: "language_id",
      headerName: translate("label.language"),
      minWidth: 90,
      flex: 1,
      renderCell: params => (params.row.language_id || "").toUpperCase()
    },
    {
      field: "actions",
      headerName: translate("label.actions"),
      minWidth: 170,
      sortable: false,
      flex: 2,
      renderCell: params => <ListActions campaign={params.row} isForecast={false} isDspDeal={false} />
    }
  ]
  if (hasError) {
    return (
      <Typography color="error" fontSize=".9rem">
        {translate("label.error.unexpected")}
      </Typography>
    )
  } else if (campaigns === null) {
    return <CircularProgress sx={{ color: theme => theme.palette.primary.main, width: "1rem" }} size="20" />
  }
  return (
    <FlexBox
      fullWidth
      vertical
      sx={{
        "minWidth": "100%",
        "maxWidth": "100%",
        "pl": ".4rem",
        "pr": ".4rem",
        "backgroundColor": theme => theme.palette.background.paper,
        "height": "100%",
        "minHeight": "100%",
        "maxHeight": "100%",
        "transition": "all 400ms ease",
        "& > *": {
          width: "100%"
        },
        "& .MuiDataGrid-root": {
          border: "none"
        },
        "& .MuiDataGrid-virtualScrollerRenderZone > div:nth-of-type(odd)": {
          backgroundColor: theme => theme.palette.background.default
        },
        "& .MuiDataGrid-virtualScrollerRenderZone > div:nth-of-type(odd):hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)"
        },
        "& .MuiDataGrid-cell": {
          fontSize: ".8rem",
          fontWeight: 300
        }
      }}>
      <DataGrid
        rows={campaigns}
        columns={columns}
        loading={campaigns === null}
        className="order-campaign-list"
        autoHeight
        localeText={getDataGridTexts()}
      />
    </FlexBox>
  )
})
